import React, { useState } from 'react';
import useDialog from '../hooks/useDialog';
import { useMonetization } from '../contexts/MonetizationContext';
import { useTranslation } from '../contexts/LanguageContext';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faGift, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './StreakMilestoneModal.css';

/**
 * StreakMilestoneModal - Celebration modal when user reaches a streak milestone.
 * Shows the milestone name, hint reward, and a claim button.
 */
function StreakMilestoneModal({ milestones, onClose }) {
    const { claimMilestone } = useMonetization();
    const { t } = useTranslation();
    const [claimingIndex, setClaimingIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [claimed, setClaimed] = useState(false);

    useBodyScrollLock(milestones && milestones.length > 0);

    const dialogRef = useDialog(true, onClose);

    if (!milestones || milestones.length === 0) {
        return null;
    }

    const current = milestones[claimingIndex];
    if (!current) return null;

    // Names are translated by day count; unknown milestones keep the server's name.
    const milestoneKey = `streak.milestones.${current.day}`;
    const translatedName = t(milestoneKey);
    const milestoneName = translatedName === milestoneKey ? current.name : translatedName;

    const handleClaim = async () => {
        setLoading(true);
        const result = await claimMilestone(current.day);
        setLoading(false);

        if (result.success) {
            setClaimed(true);
            setTimeout(() => {
                setClaimed(false);
                if (claimingIndex < milestones.length - 1) {
                    setClaimingIndex(prev => prev + 1);
                } else {
                    onClose();
                }
            }, 1200);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget && !loading) {
            onClose();
        }
    };

    return (
        <div className="modal-overlay-base milestone-overlay" onClick={handleOverlayClick}>
            <div className="modal-content-base milestone-modal" data-modal-scrollable ref={dialogRef} role="dialog" aria-modal="true">
                {!loading && (
                    <button
                        className="milestone-close"
                        onClick={onClose}
                        aria-label={t('common.close')}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                )}

                <div className="milestone-header">
                    <div className="milestone-fire">
                        <span role="img" aria-label={t('streak.milestone.fireLabel')}>&#x1F525;</span>
                    </div>
                    <h2>{milestoneName}</h2>
                    <p className="milestone-streak">
                        {t('streak.milestone.dayStreak', { count: current.day })}
                    </p>
                </div>

                <div className="milestone-reward">
                    <FontAwesomeIcon icon={faGift} />
                    <span>{t.rich('streak.milestone.reward', { b: <strong /> }, { count: current.hints })}</span>
                </div>

                {milestones.length > 1 && (
                    <p className="milestone-count">
                        {t('streak.milestone.progress', { current: claimingIndex + 1, total: milestones.length })}
                    </p>
                )}

                <button
                    className={`milestone-claim-button ${claimed ? 'claimed' : ''}`}
                    onClick={handleClaim}
                    disabled={loading || claimed}
                >
                    {loading ? (
                        <><FontAwesomeIcon icon={faSpinner} spin /> {t('streak.milestone.claiming')}</>
                    ) : claimed ? (
                        t('streak.milestone.claimed')
                    ) : (
                        t('streak.milestone.claim')
                    )}
                </button>
            </div>
        </div>
    );
}

export default StreakMilestoneModal;
